import { useLazyQuery, useQuery } from '@apollo/client';
import {
  ActionButton,
  ContextualMenu,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  DirectionalHint,
  IColumn,
  Icon,
  IDetailsRowProps,
  IDetailsRowStyles,
  IDropdownOption,
  IGroup,
  PrimaryButton,
  SelectionMode,
  Separator,
  Stack,
  TooltipHost,
  useTheme,
} from '@fluentui/react';
import { DangerButton } from 'common/components/DangerButton';
import { ModalWrapper } from 'common/components/ModalWrapper';

import { dateFormat } from 'common/utils/dateFormats';
import React, { JSX, useEffect, useState } from 'react';
import ApprovedIcon from '../../../assets/svgs/ApproveStatusIcons/Approved.svg?react';
import DeclinedIcon from '../../../assets/svgs/ApproveStatusIcons/Declined.svg?react';
import PendingIcon from '../../../assets/svgs/ApproveStatusIcons/Pending.svg?react';
import { ApprovalHistory_approvalHistory_iconType } from '../__generated__/ApprovalHistory';
import {
  ApprovalHistoryBranches,
  ApprovalHistoryBranches_approvalHistoryBranches_nodes,
  ApprovalHistoryBranchesVariables,
} from './__generated__/ApprovalHistoryBranches';
import { useColumns } from './column.data';
import { Header } from './Header';
import { ReplaceApproverModal } from './ReplaceRemoveApproverModal';
import ShimmerView from './ShimmerView';
import APPROVAL_HISTORY_BRANCHES from './ApprovalHistoryBranches.graphql';
import { AddNewApproverModal } from './AddApproverModal';

import AVAILABLE_APPROVER_LIST from './AvailableEntityApproverList.graphql';
import {
  AvailableEntityApproverList,
  AvailableEntityApproverListVariables,
} from './__generated__/AvailableEntityApproverList';

export interface ReplaceRemoveApproverProps {
  entityApprovalId: string | undefined;
  approvalType: string | null | undefined;
  iconType: ApprovalHistory_approvalHistory_iconType | null | undefined;
  onDismiss: () => void;
}

export const ReplaceRemoveApprover: React.FC<ReplaceRemoveApproverProps> = ({
  entityApprovalId,
  approvalType,
  onDismiss,
  iconType,
}) => {
  const { columns } = useColumns();
  const [replaceApprover, setReplaceApprover] = useState<boolean>(false);
  const [removeApprover, setRemoveApprover] = useState<boolean>(false);
  const [addApprover, setAddApprover] = useState<boolean>(false);

  const [entityItem, setEntityItem] = useState<
    ApprovalHistoryBranches_approvalHistoryBranches_nodes | undefined
  >(undefined);
  const { data: approvalHistoryBranchesData, loading } = useQuery<
    ApprovalHistoryBranches,
    ApprovalHistoryBranchesVariables
  >(APPROVAL_HISTORY_BRANCHES, {
    variables: {
      id: entityApprovalId!,
    },
    skip: !entityApprovalId,
  });
  const { data: availableApproversList } = useQuery<
    AvailableEntityApproverList,
    AvailableEntityApproverListVariables
  >(AVAILABLE_APPROVER_LIST, {
    variables: {
      entityApprovalId: entityApprovalId!,
    },
    skip: !entityApprovalId,
  });

  const approverListOptions: IDropdownOption[] =
    availableApproversList?.availableEntityApproverList?.nodes?.map((item) => ({
      key: item.id,
      text: item.user?.name ?? '',
    })) || [];
  // Define groups based on treeApprovalLevel
  const groups: IGroup[] = [];
  const groupedByLevel: Record<number, { startIndex: number; count: number }> =
    {};
  approvalHistoryBranchesData?.approvalHistoryBranches?.nodes.forEach(
    (item, index) => {
      if (item.treeApprovalLevel && !groupedByLevel[item.treeApprovalLevel]) {
        groupedByLevel[item.treeApprovalLevel] = {
          startIndex: index,
          count: 0,
        };
        groups.push({
          key: `group-${item.treeApprovalLevel}`,
          name: `${item.treeDescription}`,
          startIndex: index,
          count: 0,
          level: 0,
        });
      }
      if (item.treeApprovalLevel)
        groupedByLevel[item.treeApprovalLevel].count++;
    }
  );

  groups.forEach((group) => {
    const level = parseInt(group.key.split('-')[1], 10);
    group.count = groupedByLevel[level].count;
  });

  const onRenderRow = (props?: IDetailsRowProps): JSX.Element | null => {
    const customRowStyles: Partial<IDetailsRowStyles> = {
      root: {
        height: 50,
        fontSize: 14,
      },
    };
    return props ? <DetailsRow {...props} styles={customRowStyles} /> : null;
  };
  const theme = useTheme();

  const IconValue = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <ApprovedIcon />;
      case 'REQUESTED':
        return (
          <Icon
            styles={{
              root: {
                fontSize: 20,
              },
            }}
            iconName={'EditContact'}
            style={{ fill: theme.palette.neutralPrimary }}
          />
        );
      case 'REJECTED':
        return <DeclinedIcon />;
      case 'PENDING':
        return <PendingIcon />;
      default:
        return null;
    }
  };
  const renderColumn = (
    item?: ApprovalHistoryBranches_approvalHistoryBranches_nodes,
    _index?: number,
    column?: IColumn
  ) => {
    if (!item || !column) return undefined;
    const fieldContent = item[
      column.fieldName as keyof ApprovalHistoryBranches_approvalHistoryBranches_nodes
    ] as string | null;
    switch (column.key) {
      case 'statusType':
        if (iconType)
          return (
            <Stack style={{ width: 50 }}>
              <TooltipHost
                directionalHint={DirectionalHint.rightCenter}
                content={item.statusType?.statusType}
              >
                {/* <StatusIcon approval iconType={iconType} /> */}
                {IconValue(item.statusType?.approvalState!)}
              </TooltipHost>
            </Stack>
          );
        break;
      case 'actionDate':
        return (
          <Stack verticalAlign="center">
            {fieldContent ? dateFormat(fieldContent) : null}
          </Stack>
        );
      case 'action':
        return (
          <Stack tokens={{ childrenGap: 20 }} horizontal verticalAlign="center">
            <ActionButton
              iconProps={{ iconName: 'FollowUser' }}
              primary
              disabled={!item._isBranchReplaceAllowed}
              text="Add"
              onClick={() => {
                setEntityItem(item);
                setAddApprover(true);
              }}
            />
            <ActionButton
              iconProps={{ iconName: 'FollowUser' }}
              primary
              disabled={!item._isBranchReplaceAllowed}
              text="Replace"
              onClick={() => {
                setEntityItem(item);
                setReplaceApprover(true);
              }}
            />
            {item._isBranchBypassAllowed ? (
              <DangerButton
                iconProps={{ iconName: 'UserRemove' }}
                text="Bypass"
                primary
                onClick={() => {
                  setEntityItem(item);
                  setRemoveApprover(true);
                }}
              />
            ) : (
              <PrimaryButton
                iconProps={{ iconName: 'UserRemove' }}
                text="Bypass"
                primary
                disabled
              />
            )}
          </Stack>
        );

      default:
        return <Stack verticalAlign="center">{fieldContent}</Stack>;
    }
  };
  return (
    <ModalWrapper
      isOpen
      isBlocking
      dragOptions={{
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
      }}
    >
      <Header
        title="Replace/ BypassApprover - "
        subTitle={`${approvalType}`}
        onDismiss={onDismiss}
      />
      <Separator />
      <Stack style={{ paddingBottom: 40 }}>
        {loading ? (
          <ShimmerView />
        ) : (
          <DetailsList
            selectionMode={SelectionMode.none}
            items={
              approvalHistoryBranchesData?.approvalHistoryBranches?.nodes ?? []
            }
            groups={groups}
            columns={columns}
            layoutMode={DetailsListLayoutMode.justified}
            groupProps={{
              showEmptyGroups: false,
              isAllGroupsCollapsed: false,
            }}
            onRenderRow={onRenderRow}
            onRenderItemColumn={renderColumn}
          />
        )}
      </Stack>
      {(replaceApprover || removeApprover) && (
        <ReplaceApproverModal
          onDismiss={() => {
            setReplaceApprover(false);
            setRemoveApprover(false);
          }}
          entityApprovalId={entityApprovalId}
          entityItem={entityItem}
          removeApprover={removeApprover}
          approverListOptions={approverListOptions}
        />
      )}
      {addApprover && entityItem?._isAddNewApproverAllowed && (
        <AddNewApproverModal
          onDismiss={() => {
            setAddApprover(false);
          }}
          entityApprovalId={entityApprovalId}
          entityItem={entityItem}
          approverListOptions={approverListOptions}
        />
      )}
    </ModalWrapper>
  );
};
